import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { voiceApi, errorMessage } from './voiceApi';
import { useVoice } from './VoiceContext';
import { alsoText, formatDuration, formatPhone, personName, whomText, cardIsRetryable, CALL_STATUS_TEXT, statusColor } from './callFormat';
import ContactSearch from './ContactSearch';
import AlsoFileUnder from './AlsoFileUnder';
import { usePhoneLookup } from './usePhoneLookup';

const AUDIO_URL_TTL_MS = 4 * 60 * 1000; // the signed URL lives 5 minutes; refetch only after it may have expired

/**
 * A single call for a permitted admin: recording (short-lived signed URL, refetched on demand),
 * transcript, the comparison transcript when one was made, and editable notes.
 */
const CallDetailDialog = ({ callId, open, onClose, onUpdated, initialSeek = null }) => {
    const voice = useVoice();
    const [call, setCall] = useState(null);
    const [error, setError] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioError, setAudioError] = useState(null);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [view, setView] = useState('dialogue'); // 'dialogue' | 'text'
    const [changingStudent, setChangingStudent] = useState(false);
    const audioRef = useRef(null);
    const audioFetchedAtRef = useRef(0);
    const audioRetriedRef = useRef(false);
    const seekDoneRef = useRef(false); // initialSeek is honoured once per open
    const contactLabel = { parent: 'Parent', parent_2: 'Parent', student: 'Student', other: 'Contact', teacher: 'Teacher' }[call?.contact_label] || 'Contact';
    // Siblings on the dialed number, offered as "also file under".
    const lookup = usePhoneLookup(call?.to_number || '', open && Boolean(call?.student));
    const setAlsoStudents = async (list) => {
        try {
            const call_log = await voice.attachCall(callId, { also_student_uuids: list.map((s) => s.uuid) });
            setCall(call_log);
            onUpdated?.(call_log);
        } catch (err) {
            setError(errorMessage(err));
        }
    };
    const seekTo = (sec) => { const a = audioRef.current; if (a && Number.isFinite(sec)) { a.currentTime = sec; a.play().catch(() => {}); } };

    const load = useCallback(async () => {
        if (!callId) return;
        try {
            const { call_log } = await voiceApi.getCall(callId);
            setCall(call_log);
            setNotes(call_log.notes || '');
            setError(null);
        } catch (err) {
            setError(errorMessage(err));
        }
    }, [callId]);

    useEffect(() => {
        if (!open) { setCall(null); setAudioUrl(null); setAudioError(null); setError(null); setChangingStudent(false); audioRetriedRef.current = false; seekDoneRef.current = false; return; }
        load();
    }, [open, load]);

    // Opened from a search hit: scroll the Dialogue view to the turn at that moment, and once the
    // audio can play, start it from there.
    const seekIndex = (() => {
        if (initialSeek == null || !Array.isArray(call?.transcript_segments)) return -1;
        let idx = -1;
        call.transcript_segments.forEach((seg, i) => { if (Number.isFinite(seg?.start) && seg.start <= initialSeek + 0.01) idx = i; });
        return idx;
    })();
    useEffect(() => {
        if (!open || seekIndex < 0) return;
        const el = document.getElementById(`voice-seg-${callId}-${seekIndex}`);
        if (el) el.scrollIntoView({ block: 'center' });
    }, [open, callId, seekIndex]);
    const onAudioCanPlay = () => {
        if (initialSeek == null || seekDoneRef.current) return;
        seekDoneRef.current = true;
        seekTo(initialSeek);
    };

    /**
     * Attach to / move to another student or teacher ({ student } / { teacher }), or detach (null). The server
     * re-derives the label and moves a student memo; the summary stays with the call.
     */
    const setContact = async (pick) => {
        try {
            const body = pick?.teacher ? { teacher_uuid: pick.teacher.uuid }
                : pick?.student ? { student_uuid: pick.student.uuid }
                : call?.teacher ? { teacher_id: null } : { student_id: null };
            const call_log = await voice.attachCall(callId, body);
            setCall(call_log);
            setChangingStudent(false);
            onUpdated?.(call_log);
        } catch (err) {
            setError(errorMessage(err));
        }
    };

    // The signed URL lives 5 minutes; fetch it when the recording is first needed.
    const fetchAudio = useCallback(async () => {
        if (!callId) return;
        try {
            const { url } = await voiceApi.recordingUrl(callId);
            audioFetchedAtRef.current = Date.now();
            setAudioUrl(url);
            setAudioError(null);
        } catch (err) {
            setAudioError(errorMessage(err));
        }
    }, [callId]);

    useEffect(() => { if (open && call?.has_recording && !audioUrl && !audioError) fetchAudio(); }, [open, call, audioUrl, audioError, fetchAudio]);

    // A media error means either the URL expired (refetch once, if it is old enough to have) or the
    // file cannot be played (say so). Never loop: a fresh URL that fails again stops here.
    const onAudioError = () => {
        const expired = Date.now() - audioFetchedAtRef.current > AUDIO_URL_TTL_MS;
        if (expired && !audioRetriedRef.current) { audioRetriedRef.current = true; fetchAudio(); return; }
        setAudioError('The recording could not be played. It may still be copying, or the file is unavailable — try again in a minute.');
    };

    const saveNotes = async () => {
        setSaving(true);
        try {
            const call_log = await voice.saveNotes(callId, notes);
            setCall(call_log);
            onUpdated?.(call_log);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const alternatives = call?.transcript_alternatives && typeof call.transcript_alternatives === 'object'
        ? Object.entries(call.transcript_alternatives) : [];
    const canRetry = Boolean(call) && cardIsRetryable(call);
    const retry = async () => {
        setRetrying(true);
        try {
            const { call_log } = await voice.retryCall(callId);
            setCall(call_log);
            onUpdated?.(call_log);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setRetrying(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus /* the softphone stays usable while reading a call */>
            <DialogTitle sx={{ pb: 0.5 }}>
                {call ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" component="span">{whomText(call)}{alsoText(call)}</Typography>
                        <Chip size="small" label={CALL_STATUS_TEXT[call.status] || call.status} color={statusColor(call.status)} variant="outlined" />
                        {call.duration_seconds != null && <Chip size="small" label={formatDuration(call.duration_seconds)} variant="outlined" />}
                    </Box>
                ) : 'Call'}
                {call && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {personName(call.admin) || 'Admin'} · {formatPhone(call.from_number)} → {formatPhone(call.to_number)}
                        {call.started_at ? ` · ${dayjs(call.started_at).format('MMM D, YYYY h:mm A')}` : ''} · #{call.id}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
                {call && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography variant="subtitle2">Contact</Typography>
                            {call.student ? (
                                <Chip size="small" color="primary" variant="outlined" label={`${personName(call.student)}${call.student.consultation_number ? ` · #${call.student.consultation_number}` : ''}`} />
                            ) : call.teacher ? (
                                <Chip size="small" color="primary" variant="outlined" label={`${personName(call.teacher)} · teacher`} />
                            ) : (
                                <Typography variant="body2" color="text.secondary">Not attached to a student or teacher</Typography>
                            )}
                            {(call.student || call.teacher) && (
                                <>
                                    <Button size="small" onClick={() => setChangingStudent((v) => !v)} sx={{ textTransform: 'none', py: 0 }}>
                                        {changingStudent ? 'Cancel' : 'Change'}
                                    </Button>
                                    <Button size="small" color="warning" onClick={() => setContact(null)} sx={{ textTransform: 'none', py: 0 }}>
                                        Detach
                                    </Button>
                                </>
                            )}
                        </Box>
                        {((!call.student && !call.teacher) || changingStudent) && (
                            <ContactSearch
                                mode="attach"
                                autoFocus={changingStudent}
                                placeholder={call.student || call.teacher ? 'Move this call to another student or teacher…' : 'Attach to a student or teacher…'}
                                onPick={(pick) => setContact(pick)}
                            />
                        )}
                        {(call.student || call.teacher) && changingStudent && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {call.student
                                    ? 'Moving the call also moves its memo and clears the "also file under" list. The summary stays with the call.'
                                    : 'The summary stays with the call.'}
                            </Typography>
                        )}
                    </Box>
                )}
                {call?.student && (
                    <AlsoFileUnder
                        primary={call.student}
                        selected={call.extra_students || []}
                        suggestions={lookup.loading ? [] : lookup.matches.filter((m) => m.kind !== 'teacher')}
                        onChange={setAlsoStudents}
                        allowSearch
                    />
                )}
                {call && (call.summary || call.transcript_status === 'COMPLETED') && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Summary</Typography>
                        {call.summary ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{call.summary}</Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {/* A call nobody spoke on transcribes to nothing, so no summary is coming. */}
                                {call.transcript ? 'The summary is being written…' : 'Nothing was said on this call, so there is no summary.'}
                            </Typography>
                        )}
                    </Box>
                )}
                {call && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Recording</Typography>
                        {call.has_recording ? (
                            <>
                                <audio ref={audioRef} controls src={audioUrl || undefined} style={{ width: '100%' }} onError={onAudioError} onCanPlay={onAudioCanPlay} />
                                {audioError && <Typography variant="body2" color="error">{audioError}</Typography>}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {call.transcript_status === 'SKIPPED' ? 'No recording for this call.' : 'Recording not available yet.'}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                Transcript {call.transcript_model ? <Typography component="span" variant="caption" color="text.secondary">({call.transcript_model}{call.transcript_mode === 'single-fallback' ? ', no speaker labels for this call' : ''})</Typography> : null}
                            </Typography>
                            {Array.isArray(call.transcript_segments) && call.transcript_segments.length > 0 && (
                                <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)}>
                                    <ToggleButton value="dialogue" sx={{ textTransform: 'none', py: 0 }}>Dialogue</ToggleButton>
                                    <ToggleButton value="text" sx={{ textTransform: 'none', py: 0 }}>Text</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </Box>
                        {Array.isArray(call.transcript_segments) && call.transcript_segments.length > 0 && view === 'dialogue' ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.75 }}>
                                {call.transcript_segments.map((seg, i) => (
                                    <React.Fragment key={i}>
                                        <Box id={`voice-seg-${callId}-${i}`} sx={{ textAlign: 'right', pt: 0.25, ...(i === seekIndex ? { borderLeft: '3px solid', borderColor: 'primary.main', pl: 0.5, ml: -1 } : {}) }}>
                                            <Chip
                                                size="small"
                                                label={seg.speaker === 'admin' ? 'Admin' : contactLabel}
                                                color={seg.speaker === 'admin' ? 'primary' : 'default'}
                                                variant={seg.speaker === 'admin' ? 'filled' : 'outlined'}
                                                onClick={call.has_recording ? () => seekTo(seg.start) : undefined}
                                                title={call.has_recording ? `Play from ${formatDuration(seg.start)}` : undefined}
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block">{formatDuration(seg.start)}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{seg.text}</Typography>
                                    </React.Fragment>
                                ))}
                            </Box>
                        ) : call.transcript ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{call.transcript}</Typography>
                        ) : (
                            <Typography variant="body2" color={call.transcript_status === 'FAILED' ? 'error' : 'text.secondary'}>
                                {call.transcript_status === 'FAILED' ? `Transcription failed: ${call.error_message || 'unknown error'}`
                                    : call.transcript_status === 'PROCESSING' ? 'Transcribing…'
                                    : call.transcript_status === 'SKIPPED' ? 'No transcript for this call.'
                                    // COMPLETED with nothing in it: the recording had no speech, so none is coming.
                                    : call.transcript_status === 'COMPLETED' ? 'No speech was picked up in this recording.'
                                    : 'Transcript not ready yet.'}
                            </Typography>
                        )}

                        {alternatives.map(([model, alt]) => (
                            <Accordion key={model} sx={{ mt: 1 }} disableGutters>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="body2">Comparison transcript · {model}{alt?.error ? ' (failed)' : ''}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color={alt?.error ? 'error' : 'text.primary'} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                        {alt?.error || alt?.text || ''}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Notes</Typography>
                        <TextField
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            size="small"
                            placeholder="What was discussed, follow-ups…"
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {canRetry && <Button color="error" onClick={retry} disabled={retrying} sx={{ mr: 'auto' }}>{retrying ? 'Retrying…' : 'Retry'}</Button>}
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" onClick={saveNotes} disabled={!call || saving || notes === (call?.notes || '')}>Save notes</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CallDetailDialog;
